import React from 'react'

import { type PromotionPlacement } from '@onr/schema'

import clientInfoScreen from '@/assets/promotions/client-info.png'
import heroScreen from '@/assets/promotions/hero.png'
import milestoneScreen from '@/assets/promotions/milestone.png'
import volunteerInfoScreen from '@/assets/promotions/volunteer-info.png'
import volunteerProfileScreen from '@/assets/promotions/volunteer-profile.png'
import { cn } from '@/lib/utils'

const PHONE_WIDTH = 128
const PHONE_HEIGHT = 277

interface PhonePreview {
  readonly screen: string
  readonly cut: number
  readonly frameWidth: number
  readonly aspectRatio: number
  readonly outerClassName: string
  readonly innerClassName: string
  readonly insetClassName?: string
}

const phonePreviews: Partial<Record<PromotionPlacement, PhonePreview>> = {
  hero: {
    screen: heroScreen,
    cut: 0.37,
    frameWidth: 0.87,
    aspectRatio: 1,
    outerClassName: 'bg-white',
    innerClassName: 'bg-white',
  },
  volunteerProfile: {
    screen: volunteerProfileScreen,
    cut: 0.74,
    frameWidth: 0.94,
    aspectRatio: 4,
    outerClassName: 'bg-mobile-surface',
    innerClassName: 'bg-mobile-surface',
  },
  volunteerInfo: {
    screen: volunteerInfoScreen,
    cut: 0.5,
    frameWidth: 0.88,
    aspectRatio: 4,
    outerClassName: 'bg-white',
    innerClassName: 'bg-white',
  },
  clientInfo: {
    screen: clientInfoScreen,
    cut: 0.86,
    frameWidth: 0.88,
    aspectRatio: 4,
    outerClassName: 'bg-white',
    innerClassName: 'bg-white',
  },
  milestone: {
    screen: milestoneScreen,
    cut: 0.545,
    frameWidth: 0.58,
    aspectRatio: 4,
    outerClassName: 'bg-mobile-surface',
    innerClassName: 'bg-white',
    insetClassName: 'px-[6%]',
  },
}

const OptionFrame = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div
    data-testid="promotion-option-frame"
    className={cn('rounded-[3px] border-2 border-blue-700 bg-blue-700/20', className)}
    style={style}
  />
)

export interface PromotionOptionPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly placement: PromotionPlacement
  readonly label: string
}

export const PromotionOptionPreview = React.forwardRef<HTMLDivElement, PromotionOptionPreviewProps>(
  ({ placement, label, className, ...props }, ref) => {
    const preview = phonePreviews[placement]
    return (
      <div
        ref={ref}
        role="img"
        aria-label={label}
        className={cn(
          'flex h-[300px] shrink-0 items-center justify-center border-b border-gray-200 bg-gray-100 select-none',
          className
        )}
        {...props}>
        {preview ? (
          <PhoneScreen preview={preview} />
        ) : (
          <div className="flex h-[260px] w-[200px] flex-col gap-2 rounded-[4px] border border-gray-300 bg-white p-3.5">
            <div className="h-3.5 w-3/5 rounded-[2px] bg-gray-200" />
            <div className="h-2 w-full rounded-[2px] bg-gray-100" />
            <div className="h-2 w-[92%] rounded-[2px] bg-gray-100" />
            <div className="h-2 w-[96%] rounded-[2px] bg-gray-100" />
            <div className="h-2 w-[70%] rounded-[2px] bg-gray-100" />
            <div className="grow" />
            <div className="h-px bg-gray-200" />
            <OptionFrame className="h-11" />
          </div>
        )}
      </div>
    )
  }
)

const PhoneScreen = ({ preview }: { preview: PhonePreview }) => {
  const cutHeight = Math.round(preview.cut * PHONE_HEIGHT)
  const frameWidth = Math.round(preview.frameWidth * PHONE_WIDTH)
  const frameHeight = Math.round(frameWidth / preview.aspectRatio)
  return (
    <div
      className="shadow-phone overflow-hidden rounded-[14px] bg-white"
      style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT }}>
      <div className="overflow-hidden" style={{ height: cutHeight }}>
        <img src={preview.screen} alt="" className="block" style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT }} />
      </div>
      <div className={cn(preview.outerClassName, preview.insetClassName)}>
        <div className={cn('flex justify-center py-1', preview.innerClassName)}>
          <OptionFrame style={{ width: frameWidth, height: frameHeight }} />
        </div>
      </div>
      <div className="overflow-hidden" style={{ height: PHONE_HEIGHT }}>
        <img
          src={preview.screen}
          alt=""
          className="block"
          style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT, marginTop: -cutHeight }}
        />
      </div>
    </div>
  )
}
